/**
 * Die Auswahl „Kategorie zuweisen …“ am DSV-Beleg (Spec 2026-10-17 „DSV-Beleg per Klick einer
 * Kategorie zuordnen“): ein automatisch gelesener Beleg (Zustellbeleg, Rechnung, DSV-Dokument) wird
 * einer Dokumentkategorie der Bestellung zugewiesen, ohne ihn herunterzuladen und wieder hochzuladen.
 * Eingebunden an zwei Stellen, `DsvBelege.tsx` (Reiter Spedition und Dokumente) und der Block
 * „Automatische Dokumente“ in `Checkliste.tsx` - eine Komponente, keine zweite Fassung.
 *
 * Ablauf (wie der Upload in `DokumenteTab.tsx`):
 *
 * 1. Kategorie waehlen: Menuknopf „Kategorie zuweisen ▾“ oeffnet eine Liste von Knoepfen. NUR das
 *    Aktivieren eines Eintrags (Klick, Enter, Leertaste) loest die Zuordnung aus - Fokus bewegen
 *    (Pfeiltasten, Home/End) tut es nie. Deshalb KEIN `<select>`: dessen `change` feuert in Chromium schon
 *    bei einem Pfeil auf dem geschlossenen Feld (WCAG 3.2.2), und die Aktion schreibt serverseitig
 *    (loest Auto-Links, kann sendungsweit gelten).
 * 2. Haengt die Bestellung an einer Sammelsendung (`sammelsendung`), fragt ein Dialog nach der
 *    Reichweite („Nur diese Bestellung“ / „Alle N Bestellungen der Sendung“, Vorbild
 *    `ReichweiteDialog`). Sonst gibt es keine Rueckfrage: mit Sendung gilt `sendung`, ohne `bestellung`.
 * 3. Erster Aufruf OHNE `rowVersion`.
 * 4. 409 mit dem bestehenden Dokument (Platz der Kategorie belegt): Rueckfrage „Ersetzen?“, bei
 *    Bestaetigung derselbe Aufruf MIT der `rowVersion` aus der 409-Antwort, bei „Abbrechen“ nichts.
 * 5. Erfolg: Meldung an der Zeile und `onZugeordnet` (der Aufrufer laedt Dokumente, Checkliste und
 *    Belege neu).
 *
 * ================= Warum die Ersetzen-Rueckfrage kein `UeberschreibDialog` ist =================
 *
 * `UeberschreibDialog` verlangt eine Begruendung (mindestens drei Zeichen) und verspricht, sie „dauerhaft
 * zu protokollieren“. Die Schnittstelle (`POST .../dsv-belege/:belegId/zuordnung`) nimmt aber nur
 * `{ docType, reichweite, rowVersion }` - eine Begruendung kaeme nirgends an. Der Dialog hier hat deshalb
 * dieselbe Huelle und Klassen (`board-editor-*`, Portal nach `document.body`), aber nur die Frage aus
 * der Spec. Wie `EntfernenDialog` gegenueber `ReichweiteDialog`: gleiches Baumuster, eigene Komponente.
 *
 * Fehler erscheinen als Meldung an der Zeile: der Servertext bei 400/403/404/409/422, sonst ein
 * allgemeiner Satz. Nur ein Versionskonflikt-409 (Upload-Form, mit `current`, OHNE `code`) oeffnet die
 * Ersetzen-Frage. Jeder 409 mit einem `code` (`datei_fehlt`, `beleg_ist_fassung`, kuenftige) ist eine
 * Verweigerung: sein `error`-Text steht als Meldung an der Zeile, es gibt weder Dialog noch Zweitaufruf.
 */
import { createPortal } from "react-dom";
import { useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { KonfliktFehler } from "../../../api/basis.ts";
import { dsvBelegZuordnen } from "../../../api/dokumente.ts";
import { fehlertext } from "../../../lib/fehlertext.ts";
import { reichweiteVorgabe } from "../../../lib/dokumentReichweite.ts";
import { Ikone } from "../../Ikone.tsx";

type ZuordnungReichweite = "bestellung" | "sendung";

export interface BelegKategorieWahlProps {
  bestellungId: string;
  belegId: string;
  dateiname: string;
  /** Dokumenttyp, unter dem der Beleg an dieser Bestellung gerade liegt (`aktuellerHubTyp`). */
  aktuellerTyp: string | null;
  /** Bereits nach Rechten gefiltert; leer: die Komponente rendert nichts. */
  typen: ReadonlyArray<{ id: string; label: string }>;
  /** `null`: keine Rueckfrage nach der Reichweite. */
  sammelsendung: { bestellungen: number } | null;
  /** Ohne Sammelsendung: `true` heisst Reichweite „sendung“, `false` „bestellung“. */
  mitSendung: boolean;
  onZugeordnet: () => void;
}

/** Die Eingaben, die der Aufrufer fuer alle Belegzeilen gleich haelt (alles ausser dem Beleg selbst). */
export type BelegKategorieEinstellung = Pick<
  BelegKategorieWahlProps,
  "typen" | "sammelsendung" | "mitSendung" | "onZugeordnet"
>;

const ALLGEMEINER_FEHLER = "Der Beleg konnte nicht zugeordnet werden.";
const KONFLIKT_FEHLER = "Die Kategorie wurde inzwischen geändert. Bitte die Ansicht neu laden.";

interface Ersetzen {
  docType: string;
  reichweite: ZuordnungReichweite;
  rowVersion: number;
  /** Name des bestehenden Dokuments aus der 409-Antwort. */
  vorhanden: string | null;
}

function ReichweiteFrage({
  docType,
  dateiname,
  kategorie,
  anzahl,
  onAbbrechen,
  onBestaetigen,
}: {
  docType: string;
  dateiname: string;
  kategorie: string;
  anzahl: number;
  onAbbrechen: () => void;
  onBestaetigen: (reichweite: ZuordnungReichweite) => void;
}) {
  const titelId = useId();
  const name = useId();
  // Wie beim Upload (`ReichweiteDialog`): die Vorgabe je Dokumenttyp ist nur ein Vorschlag.
  const [gewaehlt, setGewaehlt] = useState<ZuordnungReichweite>(() =>
    reichweiteVorgabe(docType) === "sendung" ? "sendung" : "bestellung",
  );
  return createPortal(
    <div
      className="board-editor-overlay open"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onAbbrechen();
      }}
    >
      <section className="board-editor-dialog reichweite-dialog" role="dialog" aria-modal="true" aria-labelledby={titelId}>
        <div className="board-editor-head">
          <div className="board-editor-head-copy">
            <h2 id={titelId}>Reichweite wählen</h2>
            <p>
              „{dateiname}“ wird als {kategorie} zugeordnet. Das kann für diese Bestellung allein oder für die
              ganze Sendung gelten.
            </p>
          </div>
          <button className="icon-btn" type="button" onClick={onAbbrechen} title="Abbrechen">
            <Ikone name="x" />
          </button>
        </div>
        <div className="board-editor-body">
          <fieldset className="reichweite-auswahl">
            <legend>Gilt für</legend>
            <label>
              <input
                type="radio"
                name={name}
                value="bestellung"
                checked={gewaehlt === "bestellung"}
                onChange={() => setGewaehlt("bestellung")}
              />
              Nur diese Bestellung
            </label>
            <label>
              <input
                type="radio"
                name={name}
                value="sendung"
                checked={gewaehlt === "sendung"}
                onChange={() => setGewaehlt("sendung")}
              />
              {`Alle ${anzahl} Bestellungen der Sendung`}
            </label>
          </fieldset>
        </div>
        <div className="board-editor-foot">
          <button className="btn-sec" type="button" onClick={onAbbrechen}>
            <Ikone name="x" />
            Abbrechen
          </button>
          <button className="btn-primary" type="button" onClick={() => onBestaetigen(gewaehlt)}>
            <Ikone name="check" />
            Zuordnen
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
}

function ErsetzenFrage({
  kategorie,
  vorhanden,
  onAbbrechen,
  onBestaetigen,
}: {
  kategorie: string;
  vorhanden: string | null;
  onAbbrechen: () => void;
  onBestaetigen: () => void;
}) {
  const titelId = useId();
  return createPortal(
    <div
      className="board-editor-overlay open"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onAbbrechen();
      }}
    >
      <section className="board-editor-dialog overwrite-dialog" role="dialog" aria-modal="true" aria-labelledby={titelId}>
        <div className="board-editor-head">
          <div className="board-editor-head-copy">
            <h2 id={titelId}>Dokument ersetzen</h2>
            <p>{`In ${kategorie} liegt bereits ${vorhanden ?? "ein Dokument"}. Ersetzen?`}</p>
          </div>
          <button className="icon-btn" type="button" onClick={onAbbrechen} title="Abbrechen">
            <Ikone name="x" />
          </button>
        </div>
        <div className="board-editor-foot">
          <button className="btn-sec" type="button" onClick={onAbbrechen}>
            <Ikone name="x" />
            Abbrechen
          </button>
          <button className="btn-primary" type="button" onClick={onBestaetigen}>
            <Ikone name="refresh-cw" />
            Ersetzen
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
}

export function BelegKategorieWahl({
  bestellungId,
  belegId,
  dateiname,
  aktuellerTyp,
  typen,
  sammelsendung,
  mitSendung,
  onZugeordnet,
}: BelegKategorieWahlProps) {
  const [laeuft, setLaeuft] = useState(false);
  const [menueOffen, setMenueOffen] = useState(false);
  const wurzelRef = useRef<HTMLSpanElement | null>(null);
  const knopfRef = useRef<HTMLButtonElement | null>(null);
  const listeRef = useRef<HTMLDivElement | null>(null);
  /** Synchrone Sperre: zwei Aenderungen im selben Zeichentakt duerfen nicht zwei Aufrufe senden. */
  const laeuftRef = useRef(false);
  const [meldung, setMeldung] = useState<{ art: "ok" | "fehler"; text: string } | null>(null);
  const [reichweiteFrage, setReichweiteFrage] = useState<string | null>(null);
  const [ersetzen, setErsetzen] = useState<Ersetzen | null>(null);

  // Beim Oeffnen steht der Fokus auf dem ersten waehlbaren Eintrag (Tastaturbedienung); ein Klick
  // ausserhalb schliesst das Menue. Beides nur Fokus bzw. Sichtbarkeit - nie eine Zuordnung.
  useEffect(() => {
    if (!menueOffen) return;
    listeRef.current?.querySelector<HTMLButtonElement>('button[role="menuitem"]:not(:disabled)')?.focus();
    const ausserhalb = (e: MouseEvent): void => {
      if (!wurzelRef.current?.contains(e.target as Node)) setMenueOffen(false);
    };
    document.addEventListener("mousedown", ausserhalb);
    return () => document.removeEventListener("mousedown", ausserhalb);
  }, [menueOffen]);

  if (typen.length === 0) return null;

  const labelVon = (docType: string): string => typen.find((t) => t.id === docType)?.label ?? docType;

  async function ausfuehren(docType: string, reichweite: ZuordnungReichweite, rowVersion?: number): Promise<void> {
    if (laeuftRef.current) return;
    laeuftRef.current = true;
    setLaeuft(true);
    setMeldung(null);
    try {
      await dsvBelegZuordnen(
        bestellungId,
        belegId,
        rowVersion === undefined ? { docType, reichweite } : { docType, reichweite, rowVersion },
      );
    } catch (fehler) {
      // Inline bleibt inline (Spec R2): die Meldung steht an der Zeile, neben dem Knopf. Ihr Text kommt aus
      // dem Uebersetzer (R1) - nie `fehler.message`, das bei Proxy-HTML "HTTP 502" hiesse. `null` (Abbruch,
      // 401) heisst: nichts melden.
      const alsFehler = (text: string | null): { art: "fehler"; text: string } | null =>
        text === null ? null : { art: "fehler", text };
      if (fehler instanceof KonfliktFehler) {
        const code = (fehler.koerper as { code?: unknown } | null)?.code;
        const stand = fehler.aktuellerStand as { rowVersion?: unknown; originalName?: unknown } | null | undefined;
        if (code !== undefined && code !== null) {
          // Der Servertext zu einem bekannten Grund ist ein ganzer Satz (z. B. "Der Beleg ist die aktuelle
          // Fassung eines aelteren Dokuments.") - ohne Kontext davor.
          setMeldung(alsFehler(fehlertext(fehler)));
        } else if (rowVersion !== undefined) {
          // Auch der Ersetzen-Aufruf kam zu spaet: jemand hat den Platz inzwischen weiter geaendert.
          setMeldung({ art: "fehler", text: KONFLIKT_FEHLER });
        } else if (typeof stand?.rowVersion === "number") {
          setErsetzen({
            docType,
            reichweite,
            rowVersion: stand.rowVersion,
            vorhanden: typeof stand.originalName === "string" ? stand.originalName : null,
          });
        } else {
          setMeldung(alsFehler(fehlertext(fehler, ALLGEMEINER_FEHLER)));
        }
      } else {
        console.error(`Beleg "${belegId}" der Bestellung "${bestellungId}" konnte nicht zugeordnet werden:`, fehler);
        setMeldung(alsFehler(fehlertext(fehler, ALLGEMEINER_FEHLER)));
      }
      return;
    } finally {
      laeuftRef.current = false;
      setLaeuft(false);
    }
    setMeldung({ art: "ok", text: `${dateiname} als ${labelVon(docType)} zugeordnet` });
    onZugeordnet();
  }

  function eintraege(): HTMLButtonElement[] {
    return Array.from(listeRef.current?.querySelectorAll<HTMLButtonElement>('button[role="menuitem"]:not(:disabled)') ?? []);
  }

  function knopfTaste(e: KeyboardEvent<HTMLButtonElement>): void {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setMenueOffen(true);
    }
  }

  function listenTaste(e: KeyboardEvent<HTMLDivElement>): void {
    const liste = eintraege();
    const index = liste.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === "ArrowDown") liste[(index + 1) % liste.length]?.focus();
    else if (e.key === "ArrowUp") liste[(index - 1 + liste.length) % liste.length]?.focus();
    else if (e.key === "Home") liste[0]?.focus();
    else if (e.key === "End") liste[liste.length - 1]?.focus();
    else if (e.key === "Escape") {
      setMenueOffen(false);
      knopfRef.current?.focus();
    } else if (e.key === "Tab") {
      setMenueOffen(false);
      return;
    } else return;
    // Fokus bewegen oder schliessen - die Taste soll weder scrollen noch den Drawer erreichen.
    e.preventDefault();
    e.stopPropagation();
  }

  /** Aktivieren eines Eintrags: erst jetzt beginnt die Zuordnung. */
  function waehlen(docType: string): void {
    setMenueOffen(false);
    if (docType === aktuellerTyp) return;
    setMeldung(null);
    if (sammelsendung) setReichweiteFrage(docType);
    else void ausfuehren(docType, mitSendung ? "sendung" : "bestellung");
  }

  return (
    // `data-permission`: der Rechtedurchgang des Drawers sperrt im Reiter Spedition sonst jedes
    // Bedienelement ohne `edit_orders` (`closest` erfasst auch die spaeter gezeichneten Eintraege). Die
    // Typliste ist bereits nach Recht je Typ gefiltert.
    <span className="beleg-kategorie" ref={wurzelRef} data-permission="manage_documents manage_payments">
      <button
        ref={knopfRef}
        type="button"
        className="chk-btn beleg-kategorie-knopf"
        aria-haspopup="menu"
        aria-expanded={menueOffen}
        aria-label={`Kategorie zuweisen für ${dateiname}`}
        disabled={laeuft}
        onClick={() => setMenueOffen((offen) => !offen)}
        onKeyDown={knopfTaste}
      >
        Kategorie zuweisen ▾
      </button>
      {menueOffen ? (
        <div
          ref={listeRef}
          className="beleg-kategorie-liste"
          role="menu"
          aria-label={`Kategorien für ${dateiname}`}
          onKeyDown={listenTaste}
        >
          {typen.map((t) => (
            <button
              key={t.id}
              type="button"
              role="menuitem"
              className="beleg-kategorie-eintrag"
              disabled={t.id === aktuellerTyp}
              onClick={() => waehlen(t.id)}
            >
              {t.id === aktuellerTyp ? `${t.label} (aktuell)` : t.label}
            </button>
          ))}
        </div>
      ) : null}
      {meldung ? (
        <span
          className="beleg-kategorie-meldung"
          role={meldung.art === "fehler" ? "alert" : "status"}
        >
          {meldung.text}
        </span>
      ) : null}
      {reichweiteFrage && sammelsendung ? (
        <ReichweiteFrage
          docType={reichweiteFrage}
          dateiname={dateiname}
          kategorie={labelVon(reichweiteFrage)}
          anzahl={sammelsendung.bestellungen}
          onAbbrechen={() => setReichweiteFrage(null)}
          onBestaetigen={(reichweite) => {
            const docType = reichweiteFrage;
            setReichweiteFrage(null);
            void ausfuehren(docType, reichweite);
          }}
        />
      ) : null}
      {ersetzen ? (
        <ErsetzenFrage
          kategorie={labelVon(ersetzen.docType)}
          vorhanden={ersetzen.vorhanden}
          onAbbrechen={() => setErsetzen(null)}
          onBestaetigen={() => {
            const { docType, reichweite, rowVersion } = ersetzen;
            setErsetzen(null);
            void ausfuehren(docType, reichweite, rowVersion);
          }}
        />
      ) : null}
    </span>
  );
}
